import { useState, useEffect } from 'react'
import Button from '@/components/Button'
import { PageMetadata } from '@/types/page'

export const metadata: PageMetadata = {
  id: 'customization-5-custom-launcher',
  category: 'customization',
  categoryName: 'Customization',
  name: 'Custom Launcher',
  icon: '🎯',
  path: '/customization/custom-launcher',
  title: 'Custom Launcher for the Zendesk Messaging Web Widget',
  description: 'Demo page to showcase replacement of the default Widget launcher with a custom design'
}

interface EventLog {
  type: 'open' | 'close' | 'unreadMessages'
  timestamp: string
  data?: any
}

export default function CustomLauncher() {
  const [unreadCount, setUnreadCount] = useState(0)
  const [showCounter, setShowCounter] = useState(false)
  const [eventLog, setEventLog] = useState<EventLog[]>([])
  const [isWidgetOpen, setIsWidgetOpen] = useState(false)

  const addEventLog = (type: EventLog['type'], data?: any) => {
    const newEvent: EventLog = {
      type,
      timestamp: new Date().toLocaleTimeString(),
      data
    }
    setEventLog(prev => [newEvent, ...prev].slice(0, 10)) // Keep last 10 events
  }

  useEffect(() => {
    document.title = `Internal Note - ${metadata.title}`

    // Add Zendesk widget script
    const script = document.createElement('script')
    script.id = 'ze-snippet'
    script.src = 'https://static.zdassets.com/ekr/snippet.js?key=a8969772-cc39-4eda-9f8c-b2adbe197a96'
    script.async = true
    document.body.appendChild(script)

    // Set up event listeners when Zendesk loads
    script.onload = () => {
      if (window.zE) {
        // Unread messages event
        window.zE('messenger', 'on', 'unreadMessages', (count: number) => {
          setUnreadCount(count)
          addEventLog('unreadMessages', { count })

          // if there are unread messages, show badge
          if (count > 0) {
            setShowCounter(true)
          }
          // if there are more than 4 unread messages, show the widget
          else if (count >= 4) {
            window.zE('messenger', 'open')
          }
          // else hide the counter
          else {
            setShowCounter(false)
          }
        })

        // Open event
        window.zE('messenger', 'on', 'open', () => {
          setIsWidgetOpen(true)
          addEventLog('open')
        })

        // Close event
        window.zE('messenger', 'on', 'close', () => {
          setIsWidgetOpen(false)
          addEventLog('close')
        })
      }
    }

    return () => {
      const existing = document.getElementById('ze-snippet')
      if (existing) existing.remove()
    }
  }, [])

  const openWidget = () => {
    if (window.zE) {
      window.zE('messenger', 'open')
    }
  }

  const demoCounter = () => {
    setUnreadCount(9)
    setShowCounter(true)
  }

  return (
    <>
      {/* Custom Launcher */}
      <div aria-live="assertive" id="showWidget" className="fixed bottom-6 right-6 z-40">
        <div className="w-full flex flex-col items-center sm:items-end">
          <div className="bg-cyan-600 hover:bg-blue-800 shadow-lg p-3 rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden">
            <div className="shrink-0 flex">
              <button
                type="button"
                onClick={openWidget}
                className="rounded-md inline-flex text-white hover:text-blue-50 focus:outline-hidden"
              >
                <span className="sr-only">Help</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Unread Counter Badge */}
      {showCounter && (
        <div className="absolute bottom-16 right-4 z-40" id="counter_blob">
          <div className="bg-red-700 shadow-lg p-1 rounded-full pointer-events-auto overflow-hidden">
            <div className="shrink-0 flex">
              <button
                type="button"
                onClick={openWidget}
                className="h-4 w-4 flex justify-center rounded-md text-white focus:outline-hidden"
              >
                <span className="font-medium text-xs">{unreadCount}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <main id="content" className="mx-auto max-w-7xl py-10 px-4 sm:px-6 md:px-8">
        <header>
          <div className="py-6 px-4 sm:px-6 lg:px-8">
            <div className="md:flex md:items-center md:justify-between md:space-x-5">
              <div className="w-2/3">
                <h1 className="text-3xl text-licorice dark:text-white">{metadata.title}</h1>
                <h2 className="mt-2 text-xl text-licorice dark:text-white">{metadata.description}</h2>
              </div>
              <div className="w-1/3 justify-stretch mt-6 flex flex-col-reverse space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-y-0 sm:space-x-3 sm:space-x-reverse md:mt-0 md:flex-row md:space-x-3">
                <Button
                as="a"
                href="https://internalnote.com/custom-launcher-for-zendesk-messaging-and-unread-counts?utm_source=widget_demo&campaign=demo"
                target="_blank"
                rel="noopener noreferrer"
              >
                Read more
              </Button>
              </div>
            </div>
          </div>
        </header>

        <div className="py-6 sm:px-6 lg:px-8 mb-32">
          <div className="max-w-3xl mx-auto">
            <p className="eading-6 text-licorice dark:text-white">
              This page has 2 ways to open the Zendesk Widget. You can either use the <em>Contact Us</em> button below, or the Custom Launcher at the bottom.
            </p>
            <div className="bg-white dark:bg-licorice border border-gray-200 sm:rounded-lg mt-8">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 text-licorice dark:text-white">Need Help?</h3>
                <div className="mt-2 max-w-xl text-sm text-licorice dark:text-white">
                  <p>Our Answer Bot is available 24/7 to assist you. Or you can talk to one of our agents directly.</p>
                </div>
                <div className="mt-5">
                  <button
                    type="button"
                    onClick={openWidget}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-xs font-medium rounded-md text-licorice dark:text-white bg-white dark:bg-licorice hover:bg-gray-50 sm:text-sm"
                  >
                    Contact Us
                  </button>
                </div>
              </div>
            </div>

            <div className="rounded-md bg-blue-50 p-4 mt-4">
              <div className="flex">
                <div className="shrink-0">
                  <svg
                    className="h-5 w-5 text-blue-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3 flex-1 md:flex md:justify-between">
                  <p className="text-sm text-blue-700">
                    This Custom Launcher implements widget event listeners: unread messages, open, and close events
                  </p>
                  <p className="mt-3 text-sm md:mt-0 md:ml-6">
                    <button
                      onClick={demoCounter}
                      className="whitespace-nowrap font-medium text-blue-700 dark:text-blue-400 hover:text-licorice dark:hover:text-white"
                    >
                      Demo Counter <span aria-hidden="true">&rarr;</span>
                    </button>
                  </p>
                </div>
              </div>
            </div>

            {/* Widget Status */}
            <div className="mt-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-licorice dark:text-white mb-2">Widget Status</h3>
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${isWidgetOpen ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {isWidgetOpen ? 'Widget is Open' : 'Widget is Closed'}
                </span>
              </div>
            </div>

            {/* Event Log */}
            <div className="mt-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-licorice dark:text-white mb-3">Event Log</h3>
              {eventLog.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 italic">No events yet. Try opening or closing the widget.</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {eventLog.map((event, index) => (
                    <div
                      key={index}
                      className={`text-xs p-2 rounded ${
                        event.type === 'open'
                          ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200'
                          : event.type === 'close'
                          ? 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200'
                          : 'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <span className="font-mono font-semibold">
                          {event.type === 'unreadMessages' ? '📬 unreadMessages' : event.type === 'open' ? '📂 open' : '📕 close'}
                        </span>
                        <span className="text-gray-500 dark:text-gray-400">{event.timestamp}</span>
                      </div>
                      {event.data && (
                        <div className="mt-1 font-mono text-xs opacity-75">
                          {JSON.stringify(event.data)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* API Reference */}
            <div className="mt-6 bg-gray-900 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-100 mb-3">Event Listeners Used:</h4>
              <pre className="text-xs text-gray-100 overflow-x-auto">
                <code>{`// Unread messages event
zE('messenger', 'on', 'unreadMessages', (count) => {
  console.log('Unread count:', count)
})

// Widget open event
zE('messenger', 'on', 'open', () => {
  console.log('Widget opened')
})

// Widget close event
zE('messenger', 'on', 'close', () => {
  console.log('Widget closed')
})`}</code>
              </pre>
            </div>
          </div>
        </div>
      </main>
    </>
  )
}

// Extend window type for Zendesk
declare global {
  interface Window {
    zE?: any
  }
}
